import axios from 'axios';
import * as Sentry from '@sentry/vue';
import { IgnorableException } from '@/modules/common/models/exception/ignorable-exception.ts';
import type { CommonError } from '@/modules/common/models/common-error.ts';
import { notification } from '@/modules/common/composables/use-notification.ts';

export function isCommonError(value: unknown): value is CommonError {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  const { error, error_code, error_message } = value as CommonError;

  return error === true && typeof error_code === 'number' && typeof error_message === 'string';
}

export class ErrorUtil {
  /** VITE_BUSINESS_ERROR_HANDLING=0 или false в рантайме */
  static businessErrorHandlingEnabled = import.meta.env.VITE_BUSINESS_ERROR_HANDLING !== '0';

  static getCommonError(error: unknown): CommonError | undefined {
    if (isCommonError(error)) {
      return error;
    }

    if (axios.isAxiosError(error) && isCommonError(error.response?.data)) {
      return error.response.data;
    }

    return undefined;
  }

  static isHttpStatus(error: unknown, code: number): boolean {
    return axios.isAxiosError(error) && error.response?.status === code;
  }

  static isBodyStatus(error: unknown, code: number): boolean {
    const data = ErrorUtil.getCommonError(error);

    return data !== undefined && data.error_code === code;
  }

  /** error_code 100–499 / 1000–1999 в теле, кроме 401/403 */
  static isBusinessError(error: unknown): boolean {
    if (!ErrorUtil.businessErrorHandlingEnabled) {
      return false;
    }

    const code = ErrorUtil.getCommonError(error)?.error_code;

    if (code === undefined || code === 401 || code === 403) {
      return false;
    }

    return (code >= 100 && code < 500) || (code >= 1000 && code < 2000);
  }

  static isUnauthorized(error: unknown): boolean {
    return ErrorUtil.isHttpStatus(error, 401) || ErrorUtil.isBodyStatus(error, 401);
  }

  static isForbidden(error: unknown): boolean {
    return ErrorUtil.isHttpStatus(error, 403) || ErrorUtil.isBodyStatus(error, 403);
  }

  static isUnexpected(error: unknown): boolean {
    return (
      !axios.isCancel(error) &&
      !(error instanceof IgnorableException) &&
      !ErrorUtil.isUnauthorized(error) &&
      !ErrorUtil.isForbidden(error) &&
      !ErrorUtil.isBusinessError(error)
    );
  }

  /**
   * Единая точка для глобальных обработчиков.
   * unexpectedToast — доп. тост для списков (403 тост только если не skipForbiddenToast).
   */
  static reportError(error: unknown, options: { unexpectedToast?: boolean; skipForbiddenToast?: boolean } = {}): void {
    if (ErrorUtil.isForbidden(error)) {
      if (!options.skipForbiddenToast) {
        notification.error('common.error.forbidden', error);
      }
      return;
    }

    const data = ErrorUtil.getCommonError(error);

    if (data && ErrorUtil.isBusinessError(error)) {
      notification.error('common.notification.error.common.get', error);
      Sentry.captureException(new Error(data.error_message), {
        extra: { error_code: data.error_code, error_message: data.error_message },
      });
      return;
    }

    if (ErrorUtil.isUnexpected(error)) {
      if (options.unexpectedToast) {
        notification.error('common.notification.error.common.get', error);
      }
      Sentry.captureException(error);
    }
  }
}
